<template>
    <div
        class="flex items-center justify-between py-3 w-full"
        aria-label="Pagination"
    >
        <!-- mobile: -->
        <div class="flex flex-1 justify-between sm:hidden">
            <div class="flex flex-col space-y-2">
                <ResultsCount
                    pClass="text-slate-500 text-sm font-normal leading-tight"
                    :startRange="startRange"
                    :endRange="endRange"
                    :totalResults="totalResults"
                />
            </div>
            <div class="flex items-center space-x-2">
                <button
                    v-if="totalPages > 1"
                    @click="prev"
                    :disabled="isInFirstPage"
                    class="relative inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-transparent"
                >
                    Previous
                </button>
                <button
                    v-if="totalPages > 1"
                    @click="next"
                    :disabled="isInLastPage"
                    class="relative inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-transparent"
                >
                    Next
                </button>
            </div>
        </div>
        <!-- desktop: -->
        <div
            class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between"
        >
            <div class="flex items-center space-x-4">
                <ResultsCount
                    pClass="text-slate-500 text-sm font-normal leading-tight"
                    :startRange="startRange"
                    :endRange="endRange"
                    :totalResults="totalResults"
                />
            </div>
            <nav
                v-if="totalPages > 1"
                class="flex justify-start items-start rounded text-slate-800 font-normal text-sm space-x-1"
                aria-label="Pagination"
            >
                <button
                    @click="prev"
                    :disabled="isInFirstPage"
                    class="h-9 flex py-2 px-1.5 rounded items-center justify-center min-w-9 leading-tight border-2 border-transparent disabled:!text-slate-800 disabled:!bg-transparent disabled:!opacity-50 hover:bg-slate-200 active:bg-slate-200 focus:border-slate-300 focus:outline-0"
                >
                    <span class="sr-only">Previous</span>
                    <Icon icon="chevron-right" svgClass="w-4 h-4 rotate-180" />
                </button>
                <button
                    v-for="page in pages"
                    :key="page.name"
                    @click="() => goToPage(page.name)"
                    aria-current="page"
                    class="h-9 flex p-2 rounded items-center justify-center min-w-9 leading-tight border-2 border-transparent focus:z-20 focus:border-slate-300 focus:outline-0 active:border-transparent"
                    :class="[
                        Number(page.name) === currentPage
                            ? 'text-orange-500 bg-gray-100 font-medium'
                            : 'text-slate-800 hover:bg-slate-200 active:bg-slate-200',
                    ]"
                >
                    {{ page.name }}
                </button>
                <button
                    @click="next"
                    :disabled="isInLastPage"
                    class="h-9 flex py-2 px-1.5 rounded items-center justify-center min-w-9 leading-tight border-2 border-transparent disabled:!text-slate-800 disabled:!bg-transparent disabled:!opacity-50 hover:bg-slate-200 active:bg-slate-200 focus:border-slate-300 focus:outline-0"
                >
                    <span class="sr-only">Next</span>
                    <Icon icon="chevron-right" svgClass="w-4 h-4" />
                </button>
            </nav>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import ResultsCount from '@/components/ui/results-count/results-count.vue';

const props = defineProps({
    totalResults: {
        type: Number,
        required: true,
    },
    totalPages: {
        type: Number,
        required: true,
    },
    perPage: {
        type: Number,
        required: true,
    },
    currentPage: {
        type: Number,
        required: true,
        default: 1,
    },
    totalResults: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['pageChanged']);

const startPage = computed(() => {
    // Ensure we never go below page 1
    const minPage = 1;
    const maxPage = Math.max(1, props.totalPages);

    if (props.currentPage === minPage) {
        return minPage;
    }

    // Calculate the start page based on current page and max visible buttons
    let start = props.currentPage - Math.floor(props.maxVisibleButtons / 2);

    // Adjust if we're near the end
    if (start + props.maxVisibleButtons > maxPage) {
        start = Math.max(minPage, maxPage - props.maxVisibleButtons + 1);
    }

    // Ensure we never go below page 1
    return Math.max(minPage, start);
});

const pages = computed(() => {
    const range = [];
    const maxPage = Math.max(1, props.totalPages);

    for (
        let i = startPage.value;
        i <= Math.min(startPage.value + props.maxVisibleButtons - 1, maxPage);
        i++
    ) {
        range.push({
            name: i,
            isDisabled: i === props.currentPage,
        });
    }
    return range;
});

const startRange = computed(() => {
    return (props.currentPage - 1) * props.perPage + 1;
});

const endRange = computed(() => {
    const end = props.currentPage * props.perPage;
    return end > props.totalResults ? props.totalResults : end;
});

const isInFirstPage = computed(() => props.currentPage === 1);
const isInLastPage = computed(() => props.currentPage === props.totalPages);

const prev = () => {
    emit('pageChanged', props.currentPage - 1);
};

const goToPage = (page) => {
    emit('pageChanged', page);
};

const next = () => {
    emit('pageChanged', props.currentPage + 1);
};

// TODO: if we ever want 'skip to last page'/'back to first' buttons
const onClickLastPage = () => {
    emit('pageChanged', props.totalPages);
};
const onClickFirstPage = () => {
    emit('pageChanged', 1);
};
</script>
